import React, { createContext, useContext, useCallback } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useCurrentUser } from '../../hooks/useApi';
import { queryKeys } from '../../lib/query-client';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
    const queryClient = useQueryClient();
    
    // Use React Query to fetch the current user
    const { 
        data: authData, 
        isLoading: loading, 
        refetch: refreshAuth 
    } = useCurrentUser();

    // Extract user information from authData
    const user = authData?.user ?? null;
    const authenticated = authData?.authenticated ?? false;

    // Login mutation
    const loginMutation = useMutation({
        mutationFn: async (credentials) => {
            const response = await fetch('/login', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(credentials),
                credentials: 'include'
            });
            return response.json();
        },
        onSuccess: (data) => {
            if (data.success) {
                // Update cached user data
                queryClient.setQueryData(queryKeys.currentUser, {
                    authenticated: true,
                    user: data.user
                });
            }
        }
    });

    // Logout mutation
    const logoutMutation = useMutation({
        mutationFn: async () => {
            const response = await fetch('/auth/logout', { credentials: 'include' });
            return response.json();
        },
        onSettled: () => {
            // Clear cache regardless of success or failure
            queryClient.setQueryData(queryKeys.currentUser, {
                authenticated: false,
                user: null
            });
            // Clear all cache
            queryClient.clear();
        }
    });

    const login = useCallback(async (credentials) => {
        try {
            const data = await loginMutation.mutateAsync(credentials);
            if (data.success) {
                return { success: true, redirectUrl: data.redirectUrl };
            } else {
                return { success: false, message: data.message || 'Login failed' };
            }
        } catch (error) {
            console.error('[AuthContext] Login error:', error);
            return { success: false, message: error.message };
        }
    }, [loginMutation]);

    const logout = useCallback(async () => {
        try {
            await logoutMutation.mutateAsync();
            console.log('[AuthContext] Logout successful');
        } catch (error) {
            console.error('[AuthContext] Logout error:', error);
        }
    }, [logoutMutation]);

    const value = {
        user,
        authenticated,
        loading,
        login,
        logout,
        refreshAuth: () => refreshAuth()
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within AuthProvider');
    }
    return context;
};
